import { ArrowRightLeft, Layers, ChevronRight, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { ScreenType } from 'types';
import { cn } from 'utils/classnames';

interface Mode {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  subItems: string[];
  screen: string;
  iconBg: string;
  iconColor: string;
  tagBg: string;
  sectionLabel?: string;
}

export const ModeSelectPage = () => {
  const { t } = useTranslation();
  const { setCurrentScreen } = useAppStore();

  const modes: Mode[] = [
    {
      id: 'move',
      label: t('modeSelect.moveLabel'),
      description: t('modeSelect.moveDescription'),
      icon: ArrowRightLeft,
      subItems: [t('modeSelect.subItemPallet')],
      screen: SCREEN_TYPES.MOVE_SCAN,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      tagBg: 'bg-blue-50 text-blue-700',
    },
    {
      id: 'pallet-build',
      label: t('modeSelect.palletBuildLabel'),
      description: t('modeSelect.palletBuildDescription'),
      icon: Layers,
      subItems: [
        t('modeSelect.subItemCreateCase'),
        t('modeSelect.subItemBuildPallet'),
        t('modeSelect.subItemWeighClose'),
      ],
      screen: SCREEN_TYPES.PALLET_BUILD,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      tagBg: 'bg-emerald-50 text-emerald-700',
      sectionLabel: t('modeSelect.sectionLabelPalletManagement'),
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="mb-2 px-1">
        <h1 className="text-2xl font-bold text-slate-800">{t('modeSelect.heading')}</h1>
        <p className="text-sm text-slate-400">{t('modeSelect.subheading')}</p>
      </div>

      {modes.map(
        ({
          id,
          label,
          description,
          icon: Icon,
          subItems,
          screen,
          iconBg,
          iconColor,
          tagBg,
          sectionLabel,
        }) => (
          <div key={id}>
            {sectionLabel && (
              <div className="mb-3 flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {sectionLabel}
                </span>
                <div className="flex-1 border-t border-slate-200" />
              </div>
            )}
            <button
              onClick={() => setCurrentScreen(screen as ScreenType)}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <div
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                  iconBg
                )}
              >
                <Icon className={cn('h-6 w-6', iconColor)} />
              </div>

              <div className="flex flex-1 flex-col items-start gap-1.5">
                <span className="text-base font-bold text-slate-800">{label}</span>
                <span className="text-xs text-left text-slate-400">{description}</span>
                <div className="flex flex-wrap gap-1.5">
                  {subItems.map((item) => (
                    <span
                      key={item}
                      className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', tagBg)}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </button>
          </div>
        )
      )}
    </div>
  );
};
